import { type Evaluated } from '@/domain/construction';
import { type Scene } from '@/engine';

import { type PdfImage } from './pdf';

/**
 * Turning the pictures in a document into something an exporter can embed.
 *
 * ## Why this is a separate step
 *
 * Every other exporter is a pure function from a scene to bytes, which is what makes them
 * testable without a browser. Images break that: the document stores a `data:` URI, and
 * turning one into pixels means an `Image` element, an event, and a wait.
 *
 * So the asynchrony is confined here. The exporters stay pure and take an already-decoded
 * map; this is the only file in the export path that touches the DOM, and it is small enough
 * to reason about in one sitting.
 *
 * ## Why re-encode rather than pass the original bytes through
 *
 * A document's image may be a PNG, a GIF or a WebP, and the only image format a PDF can carry
 * without a decompressor is baseline JPEG. Rather than implement three decoders and a deflate
 * compressor, the picture is drawn onto a canvas and read back as JPEG — which is one call,
 * uses the browser's own decoders, and produces exactly the format `/DCTDecode` wants.
 *
 * It costs a re-encode. For a scanned figure in a worksheet that is invisible; for a
 * photograph it is a generation of JPEG loss, which is the right price for a printed sheet.
 *
 * ## Why there is a size cap
 *
 * A phone photograph is four thousand pixels across and would go into the file at full
 * resolution, for a worksheet that prints it two inches wide. Capping the longest side keeps
 * a PDF that can be emailed, and the cap is well above what any printer resolves at that size.
 */

/** The longest side an embedded image is re-encoded at, in pixels. */
export const MAX_EMBEDDED_PIXELS = 1400;

/** JPEG quality for embedding. High enough that a scanned diagram stays crisp. */
const QUALITY = 0.86;

/** Every distinct picture the scene draws. */
export function imageHrefsIn(scene: Scene): readonly string[] {
  const hrefs = new Set<string>();
  for (const value of scene.values.values()) {
    if (value !== null && isImage(value)) hrefs.add(value.href);
  }
  return [...hrefs];
}

function isImage(value: Evaluated): value is Extract<Evaluated, { kind: 'image' }> {
  return value.kind === 'image';
}

/**
 * Decodes and re-encodes every picture in the scene.
 *
 * A picture that cannot be decoded is **left out** rather than throwing. One broken image must
 * not cost a teacher the export of a whole worksheet — the rest of the drawing is still worth
 * having, and the gap is visible where an exception would only be baffling.
 */
export async function decodeImagesFor(scene: Scene): Promise<ReadonlyMap<string, PdfImage>> {
  const decoded = new Map<string, PdfImage>();

  for (const href of imageHrefsIn(scene)) {
    const image = await encodeOne(href).catch(() => null);
    if (image !== null) decoded.set(href, image);
  }

  return decoded;
}

/**
 * Decoded images for the raster exporter, which paints through the ordinary painters.
 *
 * Separate from `decodeImagesFor` because a canvas wants the decoded element, not re-encoded
 * JPEG bytes — going through JPEG would cost a generation of loss for no reason at all.
 *
 * Awaited before painting rather than filled in as decodes land: an export has no next frame
 * to catch up on, so a picture that is not ready when the paint runs is a picture missing from
 * the file.
 */
export async function decodeForCanvas(
  values: readonly Evaluated[]
): Promise<ReadonlyMap<string, CanvasImageSource>> {
  const decoded = new Map<string, CanvasImageSource>();

  const hrefs = new Set(values.filter(isImage).map((value) => value.href));
  for (const href of hrefs) {
    const image = await load(href).catch(() => null);
    if (image !== null) decoded.set(href, image);
  }

  return decoded;
}

async function encodeOne(href: string): Promise<PdfImage> {
  const source = await load(href);

  const longest = Math.max(source.naturalWidth, source.naturalHeight);
  const factor = longest > MAX_EMBEDDED_PIXELS ? MAX_EMBEDDED_PIXELS / longest : 1;
  const width = Math.max(1, Math.round(source.naturalWidth * factor));
  const height = Math.max(1, Math.round(source.naturalHeight * factor));

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;

  const ctx = canvas.getContext('2d');
  if (ctx === null) throw new Error('no 2D context');

  /*
   * Painted onto white first. JPEG has no alpha channel, so a transparent PNG would otherwise
   * come back with its transparent regions as whatever the canvas happened to hold — in
   * practice black, which turns a diagram with a clear background into a black rectangle.
   */
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, width, height);
  ctx.drawImage(source, 0, 0, width, height);

  const dataUrl = canvas.toDataURL('image/jpeg', QUALITY);
  const comma = dataUrl.indexOf(',');
  if (comma < 0 || !dataUrl.startsWith('data:image/jpeg')) {
    throw new Error('the browser did not produce a JPEG');
  }

  // `atob` yields one character per byte, which is exactly what the PDF writer's Latin-1
  // encoder consumes — so the bytes reach the file without a second conversion.
  return { width, height, bytes: atob(dataUrl.slice(comma + 1)) };
}

function load(href: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new globalThis.Image();
    image.addEventListener('load', () => {
      resolve(image);
    });
    image.addEventListener('error', () => {
      reject(new Error(`could not decode ${href.slice(0, 32)}`));
    });
    image.src = href;
  });
}
